import { useCallback, useMemo } from "react";
import { useT } from "../i18n";
import { Knopf, Punkt } from "./index";
import { usePlanung } from "../daten/haken";
import { heuteTag } from "../daten/nachschlagen";
import type { Merkposten, Teilposten } from "../daten/merkliste";
import { reserviertSatz } from "../daten/reserviertsatz";

/* ══════════════════════════════════════════════════════════════════
   Frei, reserviert, belegt — die Stücke eines Postens im Zeitraum.

   Luca, 18.09.2026: *„Das muss als Option angezeigt werden in der Planung,
   aber es ist reserviert, solange die Bindungsfrist vom Angebot gilt. …
   das muss dann manuell genehmigt werden, dass man das Gerät trotzdem
   nehmen will."*

   Eine Vormerkung blockt nicht — aber sie wird auch nicht stillschweigend
   übergangen. Ein Stück, das ein anderes Angebot hält, steht deshalb nicht
   unter den freien, sondern daneben, mit Angebot und Frist.

   **Hier und nicht im Postenblatt** (Systemprüfung N3, 24.09.2026). Lisa:
   Der Knopf „Trotzdem nehmen" sitze versteckt im Blatt zum Posten; er
   gehöre in „Jetzt vermieten". Der Abschluss kannte nur die Zählung (frei /
   reserviert) aus dem Nachschlagen, nicht das Stück — das ermittelte erst
   das Blatt. Jetzt fragen beide dieselbe Stelle, mit derselben Abfrage
   (`usePlanung`, derselbe Schlüssel — wer beides öffnet, fragt einmal).
   ══════════════════════════════════════════════════════════════════ */

/** Die Stücke dieses Typs im Fenster: frei und nur reserviert. */
export function useStueckeImFenster(p: Merkposten) {
  const { data: plan } = usePlanung(p.von, p.bis);
  const ressourcen = useMemo(() => plan?.ressourcen ?? [], [plan]);
  const belegungen = useMemo(() => plan?.belegungen ?? [], [plan]);

  const heute = heuteTag(); // Tag des Geräts, nicht UTC (N7-07)
  const stuecke = useMemo(
    () => ressourcen
      .filter((r) => r.gruppe === "maschine" && r.typ_id === p.typId)
      .sort((a, b) => a.id.localeCompare(b.id, "de", { numeric: true })),
    [ressourcen, p.typId],
  );
  /** Die laufende Vormerkung eines Angebots auf diesem Stück, wenn es eine gibt. */
  const reservierungAuf = useCallback(
    (res: string) => belegungen.find(
      (b) => b.res === res && b.fest === false && (!b.optionBis || b.optionBis >= heute),
    ),
    [belegungen, heute],
  );
  const hartBelegt = useCallback(
    (res: string) => belegungen.some((b) => b.res === res && b.fest !== false),
    [belegungen],
  );

  /** Die Stücke dieses Typs, die im Fenster frei sind — nach Kennung sortiert,
   *  damit dieselbe Wahl zweimal dieselbe ist. */
  const freieStuecke = useMemo(
    () => stuecke.filter((r) => !hartBelegt(r.id) && !reservierungAuf(r.id)),
    [stuecke, hartBelegt, reservierungAuf],
  );

  /** Was es gäbe — wenn nicht ein Angebot es hielte. */
  const reservierteStuecke = useMemo(
    () => stuecke
      .filter((r) => !hartBelegt(r.id))
      .map((r) => ({ stueck: r, wer: reservierungAuf(r.id) }))
      .filter((x): x is { stueck: typeof x.stueck; wer: NonNullable<typeof x.wer> } => Boolean(x.wer)),
    [stuecke, hartBelegt, reservierungAuf],
  );

  return { freieStuecke, reservierteStuecke };
}

/**
 * Je reserviertem Stück eine Zeile: wem es versprochen ist, bis wann — und
 * „Trotzdem nehmen". Die Bestätigung geht als `reservierung_uebergehen` mit;
 * ohne sie weist die API mit `belegung.reserviert` ab.
 */
export function ReservierteStuecke({ p, aendern, klasse = "pb-reserviert" }: {
  p: Merkposten;
  aendern: (id: string, teil: Teilposten) => void;
  /** Die Klasse je Ort — im Blatt und in der Zeile des Abschlusses sieht sie verschieden aus. */
  klasse?: string;
}) {
  const t = useT();
  const { freieStuecke, reservierteStuecke } = useStueckeImFenster(p);
  return (
    <>
      {reservierteStuecke.map(({ stueck: s, wer }) => {
        const gewaehlt = p.maschine === s.id && p.reservierungUebergehen === true;
        /* Angebot, Auftrag im Abschluss oder „wird gerade vermietet" — und
           ohne Frist kein „bis" (N7-05). */
        const satz = reserviertSatz(s.id, wer);
        return (
          <p className={klasse} key={s.id}>
            <Punkt art={satz.nehmbar ? (gewaehlt ? "acht" : "gut") : "stopp"} />
            <span>{t(satz.schluessel, satz.werte)}</span>
            {satz.nehmbar ? (
              <Knopf
                klein
                aria-pressed={gewaehlt}
                onClick={() => aendern(p.id, gewaehlt
                  ? { maschine: freieStuecke[0]?.id ?? "", reservierungUebergehen: false }
                  : { maschine: s.id, reservierungUebergehen: true })}
              >
                {t(gewaehlt ? "schnell.geraetReserviertZurueck" : "schnell.geraetReserviertNehmen")}
              </Knopf>
            ) : null}
          </p>
        );
      })}
    </>
  );
}
